import type { ProvetPayPaymentListItemAttributes } from '@/models/reports/provetPayPaymentReport.js'

export const PROVET_PAY_PAYMENTS_STALE_TIME = 1000 * 5 * 60 // 5 minutes

/**
 * Payout and fee data (payout_amount, nh_markup, nh_processing_fee, other_fees) is unavailable
 * before this date due to missing BPAR data source. Using a clean month boundary (2024-10-01)
 * instead of the exact data availability date (2024-09-17).
 */
export const PROVET_PAY_PAYMENTS_MIN_DATE = '2024-10-01'

export const PROVET_PAY_PAYMENTS_RESOURCE_TYPES = {
  FILTERS: 'provetpay_filters',
  GROUP: 'provetpay_aggregation_group',
  PAYMENT: 'provetpay_payment',
} as const

export const PROVET_PAY_PAYMENTS_REPORT_COLUMNS = {
  AccountDescription: {
    id: 'account_description',
    accessorKey: 'attributes.account_description',
  },
  Client: {
    id: 'client',
    accessorKey: 'attributes.client',
  },
  Currency: {
    id: 'currency',
    accessorKey: 'attributes.currency',
  },
  Fees: {
    id: 'fees',
    accessorKey: undefined, // Computed: (nordhealth_fee ?? 0) + other_fees
  },
  Net: {
    id: 'net',
    accessorKey: undefined, // Computed: payment_amount - fees
  },
  NhMarkup: {
    id: 'nh_markup',
    accessorKey: 'attributes.nh_markup',
  },
  NhProcessingFee: {
    id: 'nh_processing_fee',
    accessorKey: 'attributes.nh_processing_fee',
  },
  NordhealthFee: {
    id: 'nordhealth_fee',
    accessorKey: 'attributes.nordhealth_fee',
  },
  OtherFees: {
    id: 'other_fees',
    accessorKey: 'attributes.other_fees',
  },
  PaidOut: {
    id: 'paid_out',
    accessorKey: 'attributes.paid_out',
  },
  PaymentAmount: {
    id: 'payment_amount',
    accessorKey: 'attributes.payment_amount',
  },
  PaymentDate: {
    id: 'payment_date_ts_utc',
    accessorKey: 'attributes.payment_date_ts_utc',
  },
  PaymentMethod: {
    id: 'payment_method',
    accessorKey: 'attributes.payment_method',
  },
  PaymentStatus: {
    id: 'payment_status',
    accessorKey: 'attributes.payment_status',
  },
  PayoutDate: {
    id: 'payout_date_ts_utc',
    accessorKey: 'attributes.payout_date_ts_utc',
  },
  Reversals: {
    id: 'reversal_amount',
    accessorKey: 'attributes.reversal_amount',
  },
  RelatedInvoice: {
    id: 'related_invoice',
    accessorKey: 'attributes.related_invoice',
  },
  Result: {
    id: 'result',
    accessorKey: 'attributes.result',
  },
  Terminal: {
    id: 'terminal',
    accessorKey: 'attributes.terminal',
  },
  TransactionId: {
    id: 'transaction_id',
    accessorKey: 'attributes.transaction_id',
  },
} as const

export const PROVET_PAY_PAYMENTS_GROUPABLE_COLUMNS = [
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.AccountDescription.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.PaymentMethod.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.PayoutDate.id,
] as const

export const PROVET_PAY_PAYMENTS_DEFAULT_COLUMN_ORDER = [
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.PaymentDate.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.AccountDescription.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.Client.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.RelatedInvoice.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.PaymentStatus.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.Terminal.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.Result.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.PaymentMethod.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.PayoutDate.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.PaidOut.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.NordhealthFee.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.NhMarkup.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.NhProcessingFee.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.OtherFees.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.Currency.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.TransactionId.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.PaymentAmount.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.Reversals.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.Fees.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.Net.id,
] as const

export const PROVET_PAY_PAYMENTS_DEFAULT_HIDDEN_COLUMNS: Partial<
  Record<(typeof PROVET_PAY_PAYMENTS_DEFAULT_COLUMN_ORDER)[number], false>
> = {
  [PROVET_PAY_PAYMENTS_REPORT_COLUMNS.AccountDescription.id]: false,
  [PROVET_PAY_PAYMENTS_REPORT_COLUMNS.Currency.id]: false,
  [PROVET_PAY_PAYMENTS_REPORT_COLUMNS.NhMarkup.id]: false,
  [PROVET_PAY_PAYMENTS_REPORT_COLUMNS.NhProcessingFee.id]: false,
  [PROVET_PAY_PAYMENTS_REPORT_COLUMNS.NordhealthFee.id]: false,
  [PROVET_PAY_PAYMENTS_REPORT_COLUMNS.OtherFees.id]: false,
  [PROVET_PAY_PAYMENTS_REPORT_COLUMNS.Terminal.id]: false,
  [PROVET_PAY_PAYMENTS_REPORT_COLUMNS.TransactionId.id]: false,
} as const
/** Client-side calculated columns with no backend field — must never be hidden by saved views. */
export const PROVET_PAY_PAYMENTS_CALCULATED_COLUMNS = [
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.Fees.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.Net.id,
  PROVET_PAY_PAYMENTS_REPORT_COLUMNS.Reversals.id,
] as const

/** Relative paths for mock-server route registration. */
export const PROVET_PAY_PAYMENTS_ENDPOINTS = {
  FILTERS: '/reports_v2/payments/provet-pay/filters',
  GROUPS: '/reports_v2/payments/provet-pay/groups',
  LIST: '/reports_v2/payments/provet-pay/',
} as const

/** Full API paths for openapi-fetch client.GET() calls. */
export const PROVET_PAY_PAYMENTS_URLS = {
  FILTERS: '/{provet_id}/api/reports_v2/payments/provet-pay/filters',
  GROUPS: '/{provet_id}/api/reports_v2/payments/provet-pay/groups',
  LIST: '/{provet_id}/api/reports_v2/payments/provet-pay/',
} as const

/** Filter keys that do not correspond to any table column. */
export const PROVET_PAY_PAYMENTS_NON_COLUMN_FILTER_KEYS = new Set([
  'date_range',
  'group_by',
  'search',
  'sort',
])

/**
 * All API fields used by the table columns and detail drawer.
 * Sent as sparse fieldsets: `fields[provetpay_payment]=…`
 * Derived from columns with accessorKeys, plus extra fields used by link cells / detail drawer.
 */
export const PROVET_PAY_PAYMENTS_DEFAULT_FIELDS_REQUEST = {
  [PROVET_PAY_PAYMENTS_RESOURCE_TYPES.PAYMENT]: [
    ...Object.values(PROVET_PAY_PAYMENTS_REPORT_COLUMNS)
      .filter((col) => col.accessorKey)
      .map((col) => col.id),
    // Extra fields not tied to a visible column but used by link cells / detail drawer
    'client_id',
    'invoice_id',
  ],
}

export const PROVET_PAY_PAYMENTS_GROUP_FIELDS_REQUEST = {
  [PROVET_PAY_PAYMENTS_RESOURCE_TYPES.GROUP]: [
    'group_by',
    'group_value',
    'payment_count',
    'refund_count',
    'dispute_count',
    'total_payment_amount',
    'total_reversals',
    'total_payment_fees',
    'net_amount',
  ],
}

/**
 * Placeholder attributes for group / load-more rows that need a full attribute shape.
 * Uses `satisfies` to ensure every key of ProvetPayPaymentListItemAttributes is present
 * and will fail to compile if the schema adds or removes a field.
 */
export const PROVET_PAY_PAYMENTS_EMPTY_ATTRIBUTES: Readonly<ProvetPayPaymentListItemAttributes> =
  {
    account_description: '',
    client: null,
    client_id: null,
    currency: null,
    invoice_id: null,
    nh_markup: 0,
    nh_processing_fee: 0,
    nordhealth_fee: 0,
    other_fees: 0,
    paid_out: '' as ProvetPayPaymentListItemAttributes['paid_out'],
    payment_amount: 0,
    payment_date_ts_utc: '',
    payment_method: '',
    payment_status: '' as ProvetPayPaymentListItemAttributes['payment_status'],
    payout_amount: 0,
    payout_date_ts_utc: null,
    related_invoice: null,
    result: '' as ProvetPayPaymentListItemAttributes['result'],
    reversal_amount: null,
    reversal_fee: null,
    terminal: '',
    transaction_id: '',
  } satisfies Record<keyof ProvetPayPaymentListItemAttributes, unknown>

export const PROVET_PAY_PAYMENTS_FIELDS_PARAM_KEY = `fields[${PROVET_PAY_PAYMENTS_RESOURCE_TYPES.PAYMENT}]`
